import type { FC } from "react";
import { useEffect, useState } from "react";
import { Breadcrumb, BreadcrumbItem, Card, Label, Spinner, TextInput, ToggleSwitch } from "flowbite-react";
import { HiHome } from "react-icons/hi";
import { toast } from "react-toastify";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import {
  getAllPaymentsSettings,
  updatePaymentsSetting,
} from "../../services/payments";
import type { PaymentsSetting } from "../../types/payments";
import { blueThemeSpinner } from "../exercises/ExerciseModal";

const MembershipSettingsPage: FC = function () {
  const [settings, setSettings] = useState<PaymentsSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingStates, setSavingStates] = useState<
    Record<string, "saving" | "saved">
  >({});
  const [debounceTimers, setDebounceTimers] = useState<
    Record<string, NodeJS.Timeout>
  >({});

  const sortSettings = (items: PaymentsSetting[]): PaymentsSetting[] => {
    return [...items].sort((a, b) => {
      if (a.key === "enable_waitlist") return -1;
      if (b.key === "enable_waitlist") return 1;
      return 0;
    });
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await getAllPaymentsSettings();
        setSettings(sortSettings(response.data));
      } catch (error) {
        console.error("Failed to fetch payment settings:", error);
        toast.error("Failed to load payment settings");
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    return () => {
      Object.values(debounceTimers).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
    };
  }, [debounceTimers]);

  const updateSetting = async (key: string, newValue: string) => {
    const originalSettings = [...settings];

    setSettings((prev) =>
      prev.map((setting) =>
        setting.key === key
          ? {
              ...setting,
              value: newValue,
            }
          : setting,
      ),
    );

    setSavingStates((prev) => ({ ...prev, [key]: "saving" }));

    try {
      await updatePaymentsSetting(key, { value: newValue });
      setSavingStates((prev) => ({ ...prev, [key]: "saved" }));

      setTimeout(() => {
        setSavingStates((prev) => {
          const newState = { ...prev };
          delete newState[key];
          return newState;
        });
      }, 2000);
    } catch (error) {
      console.error("Failed to update setting:", error);
      toast.error("Failed to update setting");
      setSettings(originalSettings);
      setSavingStates((prev) => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
    }
  };

  const handleToggleChange = (key: string, checked: boolean) => {
    updateSetting(key, checked.toString());
  };

  const handleTextInputChange = (key: string, value: string) => {
    setSettings((prev) =>
      prev.map((setting) =>
        setting.key === key ? { ...setting, value } : setting,
      ),
    );

    if (debounceTimers[key]) {
      clearTimeout(debounceTimers[key]);
    }

    const timer = setTimeout(() => {
      updateSetting(key, value);
      setDebounceTimers((prev) => {
        const newTimers = { ...prev };
        delete newTimers[key];
        return newTimers;
      });
    }, 500);

    setDebounceTimers((prev) => ({ ...prev, [key]: timer }));
  };

  const isBoolean = (value: string): boolean => {
    return value.toLowerCase() === "true" || value.toLowerCase() === "false";
  };

  const getBooleanValue = (value: string): boolean => {
    return value.toLowerCase() === "true";
  };

  const formatKey = (key: string): string => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <NavbarSidebarLayout isFooter={false}>
      <div className="p-4">
        <div className="max-w-4xl">
          <div className="mb-3">
            <Breadcrumb className="mb-3">
              <BreadcrumbItem href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">Home</span>
                </div>
              </BreadcrumbItem>
              <BreadcrumbItem>Membership</BreadcrumbItem>
              <BreadcrumbItem href="/membership/settings">
                Settings
              </BreadcrumbItem>
            </Breadcrumb>
          </div>
          <div className="mb-3 block text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
            Membership Settings
          </div>

          {loading ? (
            <div className="flex justify-center items-center p-6">
              <Spinner theme={blueThemeSpinner} color="info" size="lg" />
              <span className="ml-3 text-gray-500 dark:text-gray-400">
                Loading settings...
              </span>
            </div>
          ) : (
            <Card className="p-4">
              <div className="space-y-3">
                {settings.length === 0 ? (
                  <div className="text-center p-6">
                    <p className="text-gray-500 dark:text-gray-400">
                      No payment settings found.
                    </p>
                  </div>
                ) : (
                  settings.map((setting) => (
                    <div
                      key={setting.id}
                      className="border-b border-gray-200 dark:border-gray-700 pb-3 last:border-b-0"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Label
                              htmlFor={setting.key}
                              className="text-base font-bold text-gray-900 dark:text-white"
                            >
                              {setting.title || formatKey(setting.key)}
                            </Label>
                            {savingStates[setting.key] && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                                {savingStates[setting.key] === "saving"
                                  ? "Saving..."
                                  : "Saved"}
                              </span>
                            )}
                          </div>
                          {setting.description && (
                            <div className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
                              {setting.description}
                            </div>
                          )}
                          <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            Updated:{" "}
                            {new Date(setting.updated_at).toLocaleString()}
                          </div>
                        </div>
                        <div className="shrink-0">
                          {isBoolean(setting.value) ? (
                            <ToggleSwitch
                              checked={getBooleanValue(setting.value)}
                              id={setting.key}
                              label=""
                              name={setting.key}
                              onChange={(checked) =>
                                handleToggleChange(setting.key, checked)
                              }
                              disabled={savingStates[setting.key] === "saving"}
                            />
                          ) : (
                            <TextInput
                              id={setting.key}
                              value={setting.value}
                              onChange={(e) =>
                                handleTextInputChange(
                                  setting.key,
                                  e.target.value,
                                )
                              }
                              className="w-40"
                              sizing="sm"
                              disabled={savingStates[setting.key] === "saving"}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          )}
        </div>
      </div>
    </NavbarSidebarLayout>
  );
};

export default MembershipSettingsPage;
